import { createModel } from '@rematch/core';
import type { RootModel } from '.';

const API_BASE = process.env.REACT_APP_SERVICE_URL_INGESTION?.replace(/\/$/, '') || '';

const FLEET_HEADERS = {
	'Content-Type': process.env.REACT_APP_CONTENT_TYPE || 'application/x-www-form-urlencoded',
	clientid: process.env.REACT_APP_CLIENT_ID || '',
	appname: process.env.REACT_APP_APP_NAME || '',
	clientsecret: process.env.REACT_APP_CLIENT_SECRET || '',
};

const INGESTION_HEADERS = {
	'Content-Type': 'application/json',
	IngestionId: process.env.REACT_APP_INGESTIONID_LLM || '',
	IngestionSecret: process.env.REACT_APP_INGESTIONSECRET_LLM || '',
};

// ── Fetch remaining chat tokens for a user ─────────────────────────────────
// GET /services/api/vc_chat_token_subscription?user_id=<id>
//
// We only care about `remaining_tokens`. Blank ("") or a failed fetch both
// mean "no real count available" and resolve to 0. Negative values (a known
// backend issue — e.g. -229952, likely a unit mismatch between a
// query-count quota and raw LLM token usage) are clamped to 0 too, so
// nothing downstream ever has to deal with a blank/negative number.
async function fetchRemainingTokens(userId: string | number): Promise<number> {
	if (!userId) return 0;

	try {
		const res = await fetch(
			`${API_BASE}/services/api/vc_chat_token_subscription?user_id=${userId}`,
			{ method: 'GET', headers: FLEET_HEADERS },
		);
		if (!res.ok) {
			console.error('Token subscription fetch failed:', res.status);
			return 0;
		}

		const rawText = await res.text();
		let data: any;
		try {
			data = rawText ? JSON.parse(rawText) : [];
		} catch (parseErr) {
			console.error('Invalid JSON from token subscription endpoint:', parseErr, rawText);
			return 0;
		}

		// API returns an array with a single record for the user.
		const record = Array.isArray(data) ? data[0] : data;
		const raw = record?.remaining_tokens;
		if (raw === '' || raw === null || raw === undefined) return 0;

		const num = Number(raw);
		return Number.isNaN(num) ? 0 : Math.max(0, num);
	} catch (err) {
		console.error('Error fetching token subscription:', err);
		return 0;
	}
}

export interface ITokenSubscriptionState {
	// null = not fetched yet for this session.
	remainingTokens: number | null;
}

const initialValue: ITokenSubscriptionState = {
	remainingTokens: null,
};

export const tokenSubscription = createModel<RootModel>()({
	state: { ...initialValue },
	reducers: {
		setRemainingTokens(state, res: number | null) {
			return {
				...state,
				remainingTokens: res,
			};
		},
	},
	effects: () => ({
		// Pulls the live remaining-token count for the logged-in user and
		// stores it — both the Fleet Sage chat page and the sidebar nav read
		// this same value, so it only needs to be fetched in one place.
		async refresh(_: void, rootState) {
			const userId = rootState.auth?.user?.user?.id || rootState.auth?.user?.user?.userId;
			if (!userId) return;
			const remaining = await fetchRemainingTokens(userId);
			this.setRemainingTokens(remaining);
		},
		// Renew hits the real backend action (POST /ingestion/dataIngestion,
		// action: "update_subscription") to reset the user's counter/tokens
		// server-side, then re-fetches the real value afterward — the
		// ingestion endpoint itself only returns { success, message }, not
		// the new count.
		async renew(_: void, rootState) {
			const userId = rootState.auth?.user?.user?.id || rootState.auth?.user?.user?.userId;
			const usermail = rootState.auth?.user?.user?.emailID || 'unknown@ravity.io';
			if (!userId) return false;

			try {
				const res = await fetch(`${API_BASE}/ingestion/dataIngestion`, {
					method: 'POST',
					headers: INGESTION_HEADERS,
					body: JSON.stringify({
						action: 'update_subscription',
						user_id: userId,
						usermail,
						// TODO: source the real account tier once that's tracked
						// somewhere in the app — hardcoded to match the confirmed
						// working Postman request for now.
						account_type: 'Standard',
						status: 'active',
						counter: 0,
						tokens: 0,
					}),
				});
				if (!res.ok) {
					console.error('Renew failed:', await res.text());
					return false;
				}
				await this.refresh();
				return true;
			} catch (err) {
				console.error('Error renewing tokens:', err);
				return false;
			}
		},
	}),
});